import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Button,
  Tab,
  Tabs,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemAvatar,
  Divider,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Delete,
  MoreVert,
  CheckCircle,
  Info,
  Warning,
  Error,
  Campaign,
  Assignment,
  Event,
  Grade,
  MarkEmailRead,
  EventNote,
  AttachFile,
  Chat as ChatIcon,
} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  fetchNotifications,
  fetchAllNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
} from "../../store/slices/notificationSlice";
import { displayDate } from "../../utils/dateUtils";
import { getNotificationNavigationPath } from "../../utils/notificationNavigation";

const Notifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { notifications, unreadCount, loading, markAllAsReadLoading } = useSelector(
    (state) => state.notifications
  );
  const chatConversations = useSelector((state) => state.chat.conversations || []);
  const chatUnreadCount = chatConversations.reduce((sum, conv) => sum + (conv.unread || 0), 0);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const isSuperAdmin = (user?.role || "").toLowerCase() === "superadmin";

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchAllNotifications({}));
    } else {
      dispatch(fetchNotifications({}));
    }
  }, [dispatch, isSuperAdmin]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event, notification) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleMarkAsRead = (notificationId) => {
    dispatch(markAsRead(notificationId));
  };

  const handleMarkAllAsRead = () => {
    if (markAllAsReadLoading || unreadCount === 0) return;
    dispatch(markAllAsRead());
  };

  const handleDelete = () => {
    if (selectedNotification) {
      dispatch(deleteNotification(selectedNotification.id));
    }
    handleMenuClose();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "chat":
        return <ChatIcon />;
      case "success":
        return <CheckCircle />;
      case "warning":
        return <Warning />;
      case "error":
        return <Error />;
      case "assignment":
        return <Assignment />;
      case "event":
        return <Event />;
      case "grade":
        return <Grade />;
      case "announcement":
        return <Campaign />;
      case "leave":
        return <EventNote />;
      default:
        return <Info />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "chat":
        return "primary.main";
      case "success":
        return "success.main";
      case "warning":
        return "warning.main";
      case "error":
        return "error.main";
      case "assignment":
        return "primary.main";
      case "event":
        return "secondary.main";
      case "grade":
        return "info.main";
      case "leave":
        return "info.main";
      default:
        return "grey.500";
    }
  };

  const unreadChats = chatConversations
    .filter((c) => (c.unread || 0) > 0)
    .map((c) => ({
      id: `chat_${c.id}`,
      title: `${c.name || (c.participants && c.participants[0]?.firstName) || "Someone"} sent a message`,
      message: c.lastMessage || "New message received",
      isRead: false,
      createdAt: c.time || c.updatedAt || new Date().toISOString(),
      type: "chat",
      conversationId: c.id,
    }));

  const combinedNotifications = [...unreadChats, ...(notifications || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const filteredNotifications = combinedNotifications.filter((notification) => {
    if (activeTab === 0) return true;
    if (activeTab === 1) return !notification.isRead;
    if (activeTab === 2) return notification.isRead;
    return true;
  });

  const displayNotifications = filteredNotifications;
  const totalUnreadCount = (unreadCount || 0) + (chatUnreadCount || 0);
  const plural = totalUnreadCount === 1 ? "" : "s";
  const subtitle = totalUnreadCount > 0 ? `${totalUnreadCount} unread notification${plural}` : "You're all caught up";

  const formatDate = (dateVal) => {
    if (!dateVal) return "";
    const d = typeof dateVal === "string" ? new Date(dateVal) : dateVal;
    if (Number.isNaN(d.getTime())) return String(dateVal);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return displayDate(d, String(dateVal));
  };

  const resolveNotificationPath = (notification) => getNotificationNavigationPath(notification, user);

  /**
   * Handle notification click - mark as read and navigate to related page
   */
  const handleNotificationClick = (notification) => {
    if (isSuperAdmin) return;
    if (notification.type === "chat" && notification.conversationId) {
      navigate("/chat", { state: { conversationId: notification.conversationId } });
      return;
    }
    // Mark as read if not already
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    // Navigate to related page if possible
    const path = resolveNotificationPath(notification);
    if (path) {
      const leaveId = notification.metadata?.leaveId;
      const requestId = notification.metadata?.requestId;
      const queryParams = [];
      if (leaveId) queryParams.push(`leaveId=${leaveId}`);
      if (requestId) queryParams.push(`requestId=${requestId}`);
      
      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : "";
      navigate(`${path}${queryString}`);
    }
  };

  return (
    <Box sx={{ pb: 3 }}>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            color="text.primary"
            sx={{ letterSpacing: "-0.02em", mb: 0.5 }}
          >
            Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9375rem" }}>
            {subtitle}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            markAllAsReadLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <MarkEmailRead />
            )
          }
          onClick={handleMarkAllAsRead}
          disabled={unreadCount === 0 || markAllAsReadLoading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1.25,
            boxShadow: 1,
          }}
        >
          {markAllAsReadLoading ? "Marking…" : "Mark all as read"}
        </Button>
      </Box>

      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              px: 2,
              pt: 1,
              minHeight: 48,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9375rem" },
              "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            <Tab label="All" />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Unread
                  {totalUnreadCount > 0 && (
                    <Chip
                      label={totalUnreadCount}
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: "0.75rem", fontWeight: 600 }}
                    />
                  )}
                </Box>
              }
            />
            <Tab label="Read" />
          </Tabs>

          <Divider />

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          <List sx={{ display: loading ? "none" : "block", py: 0 }}>
            {displayNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    py: 2,
                    px: 2.5,
                    alignItems: "flex-start",
                    bgcolor: notification.isRead ? "transparent" : "grey.50",
                    borderLeft: notification.isRead ? "none" : "4px solid",
                    borderColor: "primary.main",
                    cursor: isSuperAdmin ? "default" : "pointer",
                    transition: isSuperAdmin ? "none" : "background-color 0.2s ease",
                    "&:hover": isSuperAdmin
                      ? { bgcolor: notification.isRead ? "transparent" : "grey.50" }
                      : { bgcolor: notification.isRead ? "action.hover" : "grey.100" },
                  }}
                  onClick={isSuperAdmin ? undefined : () => handleNotificationClick(notification)}
                  disablePadding
                >
                  <ListItemAvatar sx={{ minWidth: 48, mt: 0.25 }}>
                    <Avatar
                      sx={{
                        bgcolor: getNotificationColor(notification.type),
                        width: 44,
                        height: 44,
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.5,
                        mb: 0.75,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={notification.isRead ? 500 : 700}
                          color="text.primary"
                          sx={{ fontSize: "1rem" }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.isRead && (
                          <Chip
                            label="New"
                            size="small"
                            color="primary"
                            sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: "nowrap", fontSize: "0.8125rem" }}
                        >
                          {formatDate(notification.createdAt)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, notification);
                          }}
                          sx={{ ml: -0.5 }}
                          aria-label="More options"
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    {(notification.createdByName || notification.audienceLabel) && (
                      <Box
                        sx={{
                          mb: 1,
                          py: 0.75,
                          px: 1.25,
                          borderRadius: 1,
                          bgcolor: "grey.100",
                          border: "1px solid",
                          borderColor: "grey.300",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.8125rem", lineHeight: 1.45 }}
                        >
                          {notification.createdByName && (
                            <>Raised by: <strong>{notification.createdByName}</strong></>
                          )}
                          {notification.createdByName && notification.audienceLabel && " · "}
                          {notification.audienceLabel && (
                            <>For: <strong>{notification.audienceLabel}</strong></>
                          )}
                        </Typography>
                      </Box>
                    )}
                    {notification.message && (
                      <Box
                        sx={{
                          py: 1,
                          px: 1.25,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderLeft: "3px solid",
                          borderLeftColor: "primary.main",
                          bgcolor: "grey.50",
                          mb: notification.metadata?.entityType === "leave" ? 1 : 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontSize: "0.9375rem", lineHeight: 1.5 }}
                        >
                          {notification.message}
                        </Typography>
                      </Box>
                    )}
                    {notification.metadata?.entityType === "leave" && (
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
                        {notification.metadata?.leaveId && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="primary"
                            sx={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const path = resolveNotificationPath(notification);
                              if (path) {
                                navigate(`${path}?leaveId=${notification.metadata.leaveId}`);
                              }
                            }}
                          >
                            View Leave
                          </Typography>
                        )}
                        {notification.metadata?.attachmentUrl && (
                          <Typography
                            component="a"
                            href={notification.metadata.attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            color="primary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.25,
                              textDecoration: "underline",
                              fontWeight: 600,
                            }}
                          >
                            <AttachFile sx={{ fontSize: 14 }} /> View Attachment
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </ListItem>
                {index < displayNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}

            {displayNotifications.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
                <NotificationsIcon
                  sx={{ fontSize: 72, color: "grey.300", mb: 2, display: "block", mx: "auto" }}
                />
                <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
                  No notifications
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  You're all caught up.
                </Typography>
              </Box>
            )}
          </List>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {selectedNotification && !selectedNotification.isRead && (
              <MenuItem
                onClick={() => {
                  handleMarkAsRead(selectedNotification.id);
                  handleMenuClose();
                }}
              >
                <MarkEmailRead sx={{ mr: 1 }} /> Mark as Read
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
              <Delete sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </Menu>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Notifications;
